import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Collapse,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import useThemeStyles from '../../../hooks/useThemeStyles';

export const PROJECT_TYPES = [
  { value: 'novel', label: 'Novel' },
  { value: 'short_story', label: 'Short Story' },
  { value: 'screenplay', label: 'Screenplay' },
  { value: 'poetry', label: 'Poetry' },
  { value: 'essay', label: 'Essay' },
  { value: 'video_game', label: 'Video Game' },
  { value: 'other', label: 'Other' },
];

const ProjectSelectionStep = ({
  projectMode,
  onProjectModeChange,
  newProjectTitle,
  onNewProjectTitleChange,
  newProjectType,
  onNewProjectTypeChange,
  projects,
  selectedProjectId,
  onSelectProject,
}) => {
  const { colors } = useThemeStyles();

  const textFieldSx = {
    '& .MuiInputLabel-root': { color: colors.text.secondary },
    '& .MuiInputLabel-root.Mui-focused': { color: colors.interactive.primary },
    input: { color: colors.text.primary },
    '& .MuiSelect-select': { color: colors.text.primary },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors.border.subtle },
      '&:hover fieldset': { borderColor: colors.interactive.primary },
      '&.Mui-focused fieldset': { borderColor: colors.interactive.primary },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ color: colors.text.secondary, textAlign: 'center' }}>
        Choose where to import your document.
      </Typography>

      <RadioGroup value={projectMode} onChange={(e) => onProjectModeChange(e.target.value)}>
        {/* Create New Project Option */}
        <FormControlLabel
          value="new"
          control={<Radio sx={{ color: colors.primary.main }} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon sx={{ color: colors.icon.primary }} />
              <Typography fontWeight={projectMode === 'new' ? 'bold' : 'normal'}>
                Create New Project
              </Typography>
            </Box>
          }
          sx={{
            border:
              projectMode === 'new' ? `2px solid ${colors.primary.main}` : '2px solid transparent',
            borderRadius: 1,
            p: 1,
            m: 0,
            backgroundColor: projectMode === 'new' ? `${colors.primary.main}10` : 'transparent',
          }}
        />

        {/* New Project Fields */}
        <Collapse in={projectMode === 'new'}>
          <Box sx={{ pl: 4, pr: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Project Title"
              value={newProjectTitle}
              onChange={(e) => onNewProjectTitleChange(e.target.value)}
              fullWidth
              placeholder="Leave empty to use file name"
              size="small"
              sx={textFieldSx}
            />
            <TextField
              select
              label="Project Type"
              value={newProjectType}
              onChange={(e) => onNewProjectTypeChange(e.target.value)}
              fullWidth
              size="small"
              sx={textFieldSx}
            >
              {PROJECT_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Collapse>

        {/* Import to Existing Project Option */}
        {projects && projects.length > 0 && (
          <>
            <FormControlLabel
              value="existing"
              control={<Radio sx={{ color: colors.primary.main }} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderIcon sx={{ color: colors.icon.primary }} />
                  <Typography fontWeight={projectMode === 'existing' ? 'bold' : 'normal'}>
                    Import to Existing Project
                  </Typography>
                </Box>
              }
              sx={{
                border:
                  projectMode === 'existing'
                    ? `2px solid ${colors.primary.main}`
                    : '2px solid transparent',
                borderRadius: 1,
                p: 1,
                m: 0,
                mt: 1,
                backgroundColor:
                  projectMode === 'existing' ? `${colors.primary.main}10` : 'transparent',
              }}
            />

            {/* Existing Projects List */}
            <Collapse in={projectMode === 'existing'}>
              <List sx={{ maxHeight: 180, overflowY: 'auto', pl: 2 }}>
                {projects.map((project) => (
                  <ListItem key={project._id} disablePadding>
                    <ListItemButton
                      selected={selectedProjectId === project._id}
                      onClick={() => onSelectProject(project._id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        py: 0.5,
                        border:
                          selectedProjectId === project._id
                            ? `1px solid ${colors.primary.main}`
                            : '1px solid transparent',
                        '&.Mui-selected': {
                          backgroundColor: `${colors.primary.main}15`,
                        },
                        '&:hover': {
                          backgroundColor: `${colors.primary.main}05`,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Radio
                          checked={selectedProjectId === project._id}
                          size="small"
                          sx={{ color: colors.primary.main }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={project.title || 'Untitled Project'}
                        secondary={
                          project.type
                            ? `${project.type} • ${project.thinChapters?.length || 0} chapters`
                            : `${project.thinChapters?.length || 0} chapters`
                        }
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: selectedProjectId === project._id ? 'bold' : 'normal',
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.75rem',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </RadioGroup>
    </Box>
  );
};

ProjectSelectionStep.propTypes = {
  projectMode: PropTypes.oneOf(['new', 'existing']).isRequired,
  onProjectModeChange: PropTypes.func.isRequired,
  newProjectTitle: PropTypes.string.isRequired,
  onNewProjectTitleChange: PropTypes.func.isRequired,
  newProjectType: PropTypes.string.isRequired,
  onNewProjectTypeChange: PropTypes.func.isRequired,
  projects: PropTypes.arrayOf(PropTypes.shape({})),
  selectedProjectId: PropTypes.string.isRequired,
  onSelectProject: PropTypes.func.isRequired,
};

ProjectSelectionStep.defaultProps = {
  projects: [],
};

export default ProjectSelectionStep;
